import { computed, reactive, ref, type ComputedRef } from "vue";
import type { PlannedTime, TimeOff } from "@/helpers/project/typesDefinition";
import { getUserName } from "@/helpers/planning/planningNormalizers";
import type {
  FilterOption,
  FilterTab,
  PlanningProject,
  PlanningUser,
} from "@/views/planning/type";
import type { PlanningTimesheetEntry } from "@/helpers/planning/planningDataNormalizers";

type PlanningPlannedTime = PlannedTime & {
  user: PlanningUser;
  project?: PlanningProject | null;
};

type PlanningTimeOff = TimeOff & {
  user: PlanningUser;
};

interface DefaultRole {
  id: string;
  name: string;
  hourlyRate: number;
}

export const usePlanningFilters = ({
  users,
  projects,
  plannedTimes,
  timeOffs,
  defaultRoles,
  timesheetEntries,
  showLoggedTime,
  showPlannedTime,
}: {
  users: ComputedRef<PlanningUser[]>;
  projects: ComputedRef<PlanningProject[]>;
  plannedTimes: ComputedRef<PlanningPlannedTime[]>;
  timeOffs: ComputedRef<PlanningTimeOff[]>;
  defaultRoles?: ComputedRef<DefaultRole[]>;
  timesheetEntries?: ComputedRef<PlanningTimesheetEntry[]>;
  showLoggedTime?: ComputedRef<boolean>;
  showPlannedTime?: ComputedRef<boolean>;
}) => {
  const showFilterPanel = ref(false);
  const filterSearch = ref("");
  const activeFilterTab = ref<FilterTab["key"]>("person");

  const filterTabs: FilterTab[] = [
    { key: "person", label: "Person" },
    { key: "role", label: "Role" },
    { key: "project", label: "Project" },
  ];

  const selectedFilters = reactive<{
    person: string[];
    role: string[];
    project: string[];
  }>({
    person: [],
    role: [],
    project: [],
  });

  const allRoles = computed<FilterOption[]>(() => {
    // If defaultRoles is provided (from default_roles table), use those
    if (defaultRoles && defaultRoles.value.length > 0) {
      return defaultRoles.value.map((role) => ({
        id: role.name, // Use role name as id for filtering
        name: role.name,
      }));
    }

    // Fallback: extract roles from users if defaultRoles not provided
    const roles = new Set<string>();

    users.value.forEach((user) => {
      if (user.role) {
        roles.add(user.role);
      }
    });

    return Array.from(roles).map((role) => ({
      id: role,
      name: role,
    }));
  });

  const filteredFilterOptions = computed<FilterOption[]>(() => {
    const search = filterSearch.value.toLowerCase();
    let options: FilterOption[] = [];

    if (activeFilterTab.value === "person") {
      options = users.value.map((user) => ({
        id: user.id,
        name: getUserName(user),
      }));
    }

    if (activeFilterTab.value === "role") {
      options = allRoles.value;
    }

    if (activeFilterTab.value === "project") {
      options = projects.value.map((project) => ({
        id: project.id,
        name: project.name,
      }));
    }

    if (!search) {
      return options;
    }

    return options.filter((option) => {
      return option.name.toLowerCase().includes(search);
    });
  });

  const filteredUsers = computed(() => {
    let result = [...users.value];

    if (selectedFilters.person.length > 0) {
      result = result.filter((user) => {
        return selectedFilters.person.includes(user.id);
      });
    }

    if (selectedFilters.role.length > 0) {
      result = result.filter((user) => {
        return Boolean(user.role && selectedFilters.role.includes(user.role));
      });
    }

    if (selectedFilters.project.length > 0) {
      // Get user IDs from planned times (if planned time is visible)
      const userIdsFromPlannedTimes = showPlannedTime?.value
        ? new Set(
            plannedTimes.value
              .filter((plannedTime) => {
                return (
                  plannedTime.project?.id &&
                  selectedFilters.project.includes(plannedTime.project.id)
                );
              })
              .map((plannedTime) => plannedTime.user.id),
          )
        : new Set<string>();

      // Get user IDs from timesheet entries (if logged time is visible)
      const userIdsFromTimesheets = showLoggedTime?.value
        ? new Set(
            (timesheetEntries?.value ?? [])
              .filter((entry) => {
                return (
                  entry.project?.id &&
                  selectedFilters.project.includes(entry.project.id)
                );
              })
              .map((entry) => entry.user.id),
          )
        : new Set<string>();

      // If both are visible, use union of both sets
      // If only one is visible, use that specific set
      const userIdsWithProject =
        showLoggedTime?.value && showPlannedTime?.value
          ? new Set([...userIdsFromPlannedTimes, ...userIdsFromTimesheets])
          : showLoggedTime?.value
          ? userIdsFromTimesheets
          : userIdsFromPlannedTimes;

      result = result.filter((user) => {
        return userIdsWithProject.has(user.id);
      });
    }

    return result;
  });

  const filteredPlannedTimes = computed(() => {
    let result = [...plannedTimes.value];

    if (selectedFilters.person.length > 0) {
      result = result.filter((plannedTime) => {
        return selectedFilters.person.includes(plannedTime.user.id);
      });
    }

    if (selectedFilters.project.length > 0) {
      result = result.filter((plannedTime) => {
        return Boolean(
          plannedTime.project?.id &&
            selectedFilters.project.includes(plannedTime.project.id),
        );
      });
    }

    return result;
  });

  const filteredTimeOffs = computed(() => {
    let result = [...timeOffs.value];

    if (selectedFilters.person.length > 0) {
      result = result.filter((timeOff) => {
        return selectedFilters.person.includes(timeOff.user.id);
      });
    }

    return result;
  });

  const hasActiveFilters = computed(() => {
    return (
      selectedFilters.person.length > 0 ||
      selectedFilters.role.length > 0 ||
      selectedFilters.project.length > 0
    );
  });

  const activeFilterCount = computed(() => {
    return (
      selectedFilters.person.length +
      selectedFilters.role.length +
      selectedFilters.project.length
    );
  });

  const toggleFilterPanel = () => {
    showFilterPanel.value = !showFilterPanel.value;
  };

  const isFilterSelected = (id: string) => {
    if (activeFilterTab.value === "person") {
      return selectedFilters.person.includes(id);
    }

    if (activeFilterTab.value === "role") {
      return selectedFilters.role.includes(id);
    }

    if (activeFilterTab.value === "project") {
      return selectedFilters.project.includes(id);
    }

    return false;
  };

  const toggleFilter = (id: string) => {
    if (activeFilterTab.value === "person") {
      toggleArrayValue(selectedFilters.person, id);
    }

    if (activeFilterTab.value === "role") {
      toggleArrayValue(selectedFilters.role, id);
    }

    if (activeFilterTab.value === "project") {
      toggleArrayValue(selectedFilters.project, id);
    }
  };

  const clearAllFilters = () => {
    selectedFilters.person = [];
    selectedFilters.role = [];
    selectedFilters.project = [];
  };

  const toggleArrayValue = <T>(array: T[], value: T) => {
    const index = array.indexOf(value);

    if (index === -1) {
      array.push(value);
      return;
    }

    array.splice(index, 1);
  };

  return {
    showFilterPanel,
    filterSearch,
    activeFilterTab,
    filterTabs,
    selectedFilters,
    filteredFilterOptions,
    filteredUsers,
    filteredPlannedTimes,
    filteredTimeOffs,
    hasActiveFilters,
    activeFilterCount,
    toggleFilterPanel,
    isFilterSelected,
    toggleFilter,
    clearAllFilters,
  };
};
